import React, { useMemo, useState } from "react";

import { get, partial, pick } from "lodash";

import { Text } from "@zapier/design-system";

import { Currency } from "@zapier/currency";
import CreditCardForm from "@zapier/payment/esm/components/CreditCardForm";
import PaymentInfo from "@zapier/payment/esm/components/PaymentInfo";
import PaymentMethodImage from "@zapier/payment/esm/components/PaymentMethodImage";
import { useSize } from "@zapier/payment/esm/hooks/useSize";
import {
  ApiKeys,
  CreditCardBrandCode,
  CreditCardPaymentSource,
  OverdueInvoice,
  PaymentDetailsCard,
  PaymentProcessor,
  PaymentSourceInput,
  PaymentSourceType,
  SupportedPaymentMethod,
} from "@zapier/payment/esm/types";

import PaymentMethod from "../PaymentMethod";
import { Aside } from "../shared-ui";

type Props = {
  accountId: number | undefined;
  /** Payment processor API keys */
  apiKeys: ApiKeys;
  /** Currently select method */
  currentMethod: PaymentSourceType | null;
  /** Error message to show */
  error?: string | undefined | null;
  /** Do not allow user to cancel card edit */
  forceCardUpdate: boolean;
  /** Hide switch component button */
  hideSwitch: boolean;
  /** Show editor */
  isEditing: boolean;
  /** Show loading */
  isEditingInFlight: boolean;
  /** Call to cancel edit */
  onCancelEdit: () => void;
  /** Call to start edit */
  onEdit: () => void;
  /** Call when editor throws an error */
  onErrorEdit: (error: Error) => void;
  /** Call to save current edit */
  onSaveEdit: (b: PaymentSourceInput) => void;
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceType) => void;
  /** User's currently saved card details */
  savedSource: CreditCardPaymentSource | null;
  /** Supported payment methods (bank, card etc.) */
  supportedMethods: Array<SupportedPaymentMethod>;
  /** The array of payment methods selectable from the "Switch Payment Method" dropdown */
  switchMethodOptions: SupportedPaymentMethod[];
  /** The selected currency */
  selectedCurrency: Currency;
  /** This is very temporary until we remove the split.io flag. This is meant to temporarily turn on localhost mode for jest tests. */
  splitLocalhostMode?: boolean;
  /** Is there an overdue invoice? */
  overdueInvoice?: OverdueInvoice;
  /** Show payment changed successfully message */
  showChangeSuccessMessage: boolean;
  /** Call to hide change success message */
  onHideSuccessMessage: () => void;
};

type UiRenderer<Props, ExtraData extends Record<string, unknown>> = (
  props: Props,
  extraData?: ExtraData
) => JSX.Element | null;

type RenderLabelProps = Pick<Props, "savedSource" | "isEditing" | "hideSwitch">;
type RenderLabelExtraData = {
  isSmall: boolean;
  cardBrand: CreditCardBrandCode | null | undefined;
};

const renderLabel: UiRenderer<RenderLabelProps, RenderLabelExtraData> = (
  props,
  extraData?
) => {
  return props.savedSource && !props.isEditing ? (
    <PaymentInfo paymentSource={props.savedSource} />
  ) : (
    <React.Fragment>
      {extraData?.isSmall && !props.hideSwitch ? null : (
        <PaymentMethodImage imageType={extraData?.cardBrand} />
      )}
      <Text tag="h3" type="paragraph1Bold">
        {props.savedSource ? "Edit card" : "Add card"}
      </Text>
    </React.Fragment>
  );
};

const PaymentMethodCard: React.FC<Props> = props => {
  const [cardBrand, setCardBrand] = useState<
    CreditCardBrandCode | null | undefined
  >(null);
  const size = useSize();
  const isSmall = size === "small";

  //@ts-ignore savedSource is not a type
  const cardDetails: PaymentDetailsCard = get(props, [
    "savedSource",
    "details",
  ]);

  const cardMethod = props.supportedMethods.find(
    m => m.method === PaymentSourceType.card
  );

  const labelProps = pick(props, ["savedSource", "isEditing", "hideSwitch"]);

  // Using useMemo here, ensures that the deps are properly populated by our exhaustive-checks hook
  const renderLabelCallback = useMemo(
    () => partial(renderLabel, labelProps, { isSmall, cardBrand }),
    [labelProps, isSmall, cardBrand]
  );

  return (
    <PaymentMethod
      isEditing={props.isEditing}
      renderAside={() => <Aside {...props} />}
      renderLabel={renderLabelCallback}
      overdueInvoice={props.overdueInvoice}
      showChangeSuccessMessage={props.showChangeSuccessMessage}
      onHideSuccessMessage={props.onHideSuccessMessage}
    >
      <CreditCardForm
        apiKeys={props.apiKeys}
        error={props.error}
        hasCancelButton={!props.forceCardUpdate}
        hasPaymentSource={Boolean(props.savedSource)}
        isInFlight={props.isEditingInFlight}
        onCancel={props.onCancelEdit}
        onCardNumberChange={setCardBrand}
        onError={props.onErrorEdit}
        onSave={props.onSaveEdit}
        paymentProcessor={get(
          cardMethod,
          "paymentProcessor",
          PaymentProcessor.stripe
        )}
        savedCard={cardDetails}
        selectedCurrency={props.selectedCurrency}
        supportedSubmethods={props.supportedMethods.map(sm => sm.submethod)}
      />
    </PaymentMethod>
  );
};

export default PaymentMethodCard;
